import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Copy, Wallet, TrendingUp, Users, Coins, Award, ArrowUpRight, Activity, CalendarClock, Flame, Target } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { money, num } from "@/lib/format";
import TradeTerminal from "@/components/portal/TradeTerminal";
import RankCelebration from "@/components/portal/RankCelebration";
import NewsPopup from "@/components/portal/NewsPopup";

const StatCard = ({ icon: Icon, label, value, accent = "amber", testid }) => (
  <div data-testid={testid} className="rounded-xl bg-[#0D0E12] border border-amber-500/15 p-5 hover:border-amber-500/30 transition-all">
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-400 uppercase tracking-wide">{label}</span>
      <Icon className={`w-5 h-5 text-${accent}-400`} />
    </div>
    <div className="font-mono-fin text-2xl font-bold mt-3">{value}</div>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [celebrate, setCelebrate] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);

  useEffect(() => {
    api.get("/me/dashboard").then(({ data }) => {
      setData(data);
      if (data.rank_celebration) setCelebrate(data.rank_celebration);
    }).catch(() => toast.error("Failed to load dashboard"));
  }, []);

  const [refLeg, setRefLeg] = useState("auto");
  const [placement, setPlacement] = useState(null);
  useEffect(() => {
    if (refLeg === "auto") { setPlacement(null); return; }
    api.get(`/me/placement-preview?position=${refLeg}`).then(({ data }) => setPlacement(data)).catch(() => setPlacement(null));
  }, [refLeg]);
  const closeCelebration = () => {
    setCelebrate(null);
    api.post("/me/ack-rank").catch(() => {});
  };

  if (!data) return <div className="text-amber-400 font-mono-fin">Loading dashboard...</div>;

  const { wallet, stats, income_breakdown, rank, user, booster, salary_tracker, investments, trading, last_income_run } = data;
  const activeInvestments = (investments || []).filter((i) => i.status === "active");
  const refLink = `${window.location.origin}/register?ref=${user.referral_code}${refLeg !== "auto" ? `&pos=${refLeg}` : ""}`;
  const copyRef = () => { navigator.clipboard.writeText(refLink); toast.success("Referral link copied"); };

  const incomes = [
    { k: "roi", l: "ROI Income (USDT 70%)", c: "emerald" },
    { k: "level", l: "Level Income", c: "sky" },
    { k: "matching", l: "Matching Income", c: "amber" },
    { k: "salary", l: "Weekly Salary", c: "purple" },
    { k: "royalty", l: "Royalty Income", c: "rose" },
    { k: "turnover", l: "Team Turnover", c: "cyan" },
    { k: "points", l: "ROI Points (30%)", c: "amber" },
  ];

  const nextMatched = rank.next?.matched || rank.current?.matched || 5000;
  const progress = Math.min(100, (stats.matched_business / nextMatched) * 100);

  return (
    <div className="space-y-6" data-testid="member-dashboard">
      <NewsPopup />
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold">Welcome, {user.name.split(" ")[0]} 👋</h1>
          <p className="text-slate-400 text-sm mt-1">Here's your wealth overview.</p>
          {last_income_run && (
            <p className="text-xs text-emerald-400 mt-1 flex items-center gap-1.5" data-testid="last-income-run">
              <Activity className="w-3.5 h-3.5" /> Daily income posted: {new Date(last_income_run).toLocaleString()}
            </p>
          )}
        </div>
        <Link to="/portal/investments" className="gold-btn px-5 py-3 rounded-xl font-bold text-sm flex items-center gap-2 self-start">
          Buy Package <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      {trading?.weekend && (
        <div className="rounded-xl bg-sky-500/10 border border-sky-500/25 px-5 py-4 flex items-start gap-3" data-testid="weekend-banner">
          <CalendarClock className="w-6 h-6 text-sky-400 shrink-0 mt-0.5" />
          <div>
            <div className="font-display font-bold text-sky-300">Markets are closed for the weekend</div>
            <p className="text-sm text-slate-300 mt-0.5">Trading and daily ROI pause on Saturday & Sunday. Everything resumes automatically on <b className="text-sky-300">Monday</b> — enjoy the break! 🌙</p>
          </div>
        </div>
      )}

      {stats.roi_booster && (
        <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/25 px-4 py-3 text-sm text-emerald-300 flex items-center gap-2" data-testid="booster-badge">
          <span className="text-lg">⚡</span> ROI Booster active — your daily ROI is boosted <b>1.5x</b>!
        </div>
      )}

      {/* wallets */}
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="glass rounded-2xl p-6 flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide">Deposit Balance</div>
            <div className="font-mono-fin text-3xl font-extrabold gold-text mt-1">{money(wallet.deposit_balance)}</div>
            <Link to="/portal/deposit" className="text-xs text-amber-400 hover:text-amber-300 mt-2 inline-block">+ Add funds</Link>
          </div>
          <Wallet className="w-10 h-10 text-amber-500/40" />
        </div>
        <div className="glass rounded-2xl p-6 flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide">Income Balance</div>
            <div className="font-mono-fin text-3xl font-extrabold text-emerald-400 mt-1">{money(wallet.income_balance)}</div>
            <Link to="/portal/withdraw" className="text-xs text-amber-400 hover:text-amber-300 mt-2 inline-block">Withdraw →</Link>
          </div>
          <Coins className="w-10 h-10 text-emerald-500/40" />
        </div>
        <div className="glass rounded-2xl p-6 flex items-center justify-between" data-testid="points-balance">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide">Points Balance</div>
            <div className="font-mono-fin text-3xl font-extrabold text-amber-300 mt-1">{Number(wallet.points_balance || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}</div>
            <span className="text-xs text-slate-500 mt-2 inline-block">Convertible to coin (soon)</span>
          </div>
          <Award className="w-10 h-10 text-amber-500/40" />
        </div>
      </div>

      {stats.income_cap > 0 && (
        <div className="glass rounded-2xl p-6" data-testid="cap-progress">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="text-sm font-semibold flex items-center gap-2"><Target className="w-4 h-4 text-amber-400" /> Earning Cap — 3X of your investment</div>
            <div className="font-mono-fin text-sm"><span className="gold-text font-bold text-lg">{money(stats.total_income)}</span> <span className="text-slate-500">of {money(stats.income_cap)}</span></div>
          </div>
          <div className="h-3.5 rounded-full bg-[#0D0E12] border border-amber-500/15 overflow-hidden">
            <div className={`h-full rounded-full transition-all ${stats.cap_pct >= 100 ? "bg-gradient-to-r from-red-500 to-amber-400" : "bg-gradient-to-r from-amber-500 to-yellow-300"}`} style={{ width: `${stats.cap_pct}%` }} data-testid="cap-progress-bar" />
          </div>
          <div className="flex items-center justify-between mt-2.5 text-xs flex-wrap gap-2">
            <span className="text-slate-400" data-testid="cap-progress-pct">{stats.cap_pct}% of your 3X limit earned</span>
            {stats.cap_pct >= 75 && (
              <Link to="/portal/investments" className="text-amber-400 hover:text-amber-300 font-semibold" data-testid="cap-retopup-link">
                {stats.cap_pct >= 100 ? "Cap reached — re-topup to keep earning →" : "You're close to your cap — add a package to keep earning →"}
              </Link>
            )}
          </div>
        </div>
      )}

      {/* stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="stat-invested" icon={TrendingUp} label="Total Invested" value={money(stats.total_invested)} />
        <StatCard testid="stat-roi" icon={Coins} label="Total ROI" value={money(stats.total_roi)} accent="emerald" />
        <StatCard testid="stat-team" icon={Users} label="Team Size" value={num(stats.team_size)} accent="sky" />
        <StatCard testid="stat-income" icon={Award} label="Total Income" value={money(stats.total_income)} accent="amber" />
      </div>

      {/* active investments with ROI range badge */}
      {activeInvestments.length > 0 && (
        <div className="glass rounded-2xl p-6" data-testid="active-investments">
          <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-amber-400" /> Active Investments</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeInvestments.map((i) => {
              const rmin = i.roi_min ?? i.roi_rate;
              const rmax = i.roi_max ?? i.roi_rate;
              const cap = i.principal * 2;
              const pct = Math.min(100, (i.roi_earned / cap) * 100);
              return (
                <div key={i.id} data-testid={`active-inv-${i.id}`} className="rounded-xl bg-[#0D0E12] border border-amber-500/20 p-5">
                  <div className="flex items-center justify-between">
                    <span className="font-display font-bold">{i.package_name}</span>
                    <span className="font-mono-fin text-amber-300 font-bold">{money(i.principal, 0)}</span>
                  </div>
                  <div className="mt-2">
                    <span data-testid={`roi-range-badge-${i.id}`} className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 font-semibold">
                      <Coins className="w-3.5 h-3.5" /> {Number(rmin).toFixed(2)}%–{Number(rmax).toFixed(2)}% Daily ROI
                    </span>
                  </div>
                  <div className="mt-3 text-xs text-slate-400 flex justify-between"><span>ROI earned</span><span className="font-mono-fin text-emerald-400">{money(i.roi_earned)} / {money(cap, 0)}</span></div>
                  <div className="h-2 rounded-full bg-black/50 overflow-hidden border border-amber-500/20 mt-1.5"><div className="h-full gold-btn transition-all" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* referral */}
      <div className="glass rounded-2xl p-6">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div className="text-xs text-slate-400 uppercase tracking-wide">Your Referral Link</div>
          <div className="flex gap-1" data-testid="referral-leg-toggle">
            {["auto", "left", "right"].map((lg) => (
              <button key={lg} data-testid={`ref-leg-${lg}`} onClick={() => setRefLeg(lg)}
                className={`text-xs px-3 py-1.5 rounded-lg font-medium capitalize transition-all ${refLeg === lg ? "gold-btn" : "text-slate-400 border border-amber-500/20 hover:text-amber-300"}`}>{lg}</button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <input data-testid="referral-link-input" readOnly value={refLink} className="flex-1 bg-black/40 border border-amber-500/20 rounded-xl px-4 py-3 font-mono-fin text-sm text-amber-300 outline-none" />
          <button data-testid="copy-referral-btn" onClick={copyRef} className="gold-btn px-4 rounded-xl font-bold flex items-center gap-2"><Copy className="w-4 h-4" /> Copy</button>
        </div>
        <p className="text-xs text-slate-500 mt-2">Placement: <b className="text-amber-300 capitalize">{refLeg === "auto" ? "Auto (spillover)" : `${refLeg} leg`}</b> — new members joining via this link are placed on your chosen leg.</p>
        {placement?.parent && (
          <div className="mt-3 rounded-xl bg-black/30 border border-amber-500/20 p-4" data-testid="placement-preview">
            <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-3">Where this invite will land</div>
            <div className="flex flex-col items-center gap-1.5">
              <div className="px-4 py-2 rounded-lg bg-[#12131A] border border-amber-500/40 text-center min-w-[9rem]">
                <div className="text-xs font-semibold">{placement.parent.is_you ? "You" : "A downline member"}</div>
                <div className="font-mono-fin text-[10px] text-amber-400">{placement.parent.is_you ? (placement.parent.username || placement.parent.referral_code) : "spillover position"}</div>
              </div>
              <div className="text-amber-500/60 text-xs font-mono-fin capitalize">↓ {placement.leg} leg</div>
              <div className="px-4 py-2 rounded-lg border-2 border-dashed border-emerald-500/40 text-center min-w-[9rem]">
                <div className="text-xs font-semibold text-emerald-400">New member</div>
                <div className="text-[10px] text-slate-500 capitalize">open {placement.leg} slot</div>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-3 text-center">
              {placement.parent.is_you ? "Placed directly under you" : "Placed via spillover in your team"} on the <span className="capitalize">{placement.leg}</span> leg.
            </p>
          </div>
        )}
        <div className="grid grid-cols-3 gap-4 mt-4 text-center">
          <div><div className="font-mono-fin text-lg font-bold text-sky-400">{money(stats.left_business, 0)}</div><div className="text-xs text-slate-500">Left Business</div></div>
          <div><div className="font-mono-fin text-lg font-bold text-amber-400">{money(stats.right_business, 0)}</div><div className="text-xs text-slate-500">Right Business</div></div>
          <div><div className="font-mono-fin text-lg font-bold text-emerald-400">{stats.directs}</div><div className="text-xs text-slate-500">Direct Referrals</div></div>
        </div>
      </div>

      {/* income breakdown + rank */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="glass rounded-2xl p-6" data-testid="income-breakdown">
          <h3 className="font-display font-bold text-lg mb-4">Income Breakdown</h3>
          <div className="space-y-3">
            {incomes.map((i) => (
              <div key={i.k} className="flex items-center justify-between">
                <span className="text-sm text-slate-300">{i.l}</span>
                <span className={`font-mono-fin font-bold text-${i.c}-400`}>{money(income_breakdown[i.k])}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="glass rounded-2xl p-6" data-testid="rank-progress">
          <h3 className="font-display font-bold text-lg mb-1">Rank Progress</h3>
          <div className="flex items-center gap-2 mb-4">
            <Award className="w-5 h-5 text-amber-400" />
            <span className="font-display font-bold gold-text">{rank.current ? rank.current.name : "No Rank Yet"}</span>
          </div>
          <div className="text-xs text-slate-400 mb-2">
            Matched: <span className="font-mono-fin text-amber-300">{money(stats.matched_business, 0)}</span>
            {rank.next && <> · Next: <span className="font-mono-fin text-amber-300">{rank.next.name} ({money(rank.next.matched, 0)})</span></>}
          </div>
          <div className="h-3 rounded-full bg-black/50 overflow-hidden border border-amber-500/20">
            <div className="h-full gold-btn transition-all" style={{ width: `${progress}%` }} />
          </div>
          {rank.next && <div className="text-xs text-slate-500 mt-2">Weekly salary at next rank: <span className="text-emerald-400 font-mono-fin">{money(rank.next.salary, 0)}</span></div>}
        </div>
      </div>

      {/* booster countdown + salary tracker */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="glass rounded-2xl p-6" data-testid="booster-tracker">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><span className="text-xl">⚡</span> ROI Booster 1.5X</h3>
            {booster?.active
              ? <span className="text-xs px-2 py-1 rounded bg-emerald-500/15 text-emerald-400 font-bold">ACTIVE</span>
              : booster?.window_open
                ? <span className="text-xs px-2 py-1 rounded bg-amber-500/15 text-amber-400 font-bold">{booster.days_left} days left</span>
                : <span className="text-xs px-2 py-1 rounded bg-red-500/15 text-red-400 font-bold">window closed</span>}
          </div>
          {booster?.active ? (
            <p className="text-sm text-emerald-300">🎉 Unlocked! Your daily ROI is permanently boosted 1.5X.</p>
          ) : (
            <>
              <p className="text-xs text-slate-400 mb-3">Get {booster?.required} direct referrals within {booster?.days_total} days of joining to unlock a permanent 1.5X ROI boost.</p>
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-slate-300">Direct Referrals</span>
                <span className="font-mono-fin text-amber-300 font-bold">{Math.min(booster?.directs || 0, booster?.required)} / {booster?.required}</span>
              </div>
              <div className="h-3 rounded-full bg-black/50 overflow-hidden border border-amber-500/20">
                <div className="h-full gold-btn transition-all" style={{ width: `${Math.min(100, ((booster?.directs || 0) / (booster?.required || 3)) * 100)}%` }} />
              </div>
              {!booster?.window_open && <p className="text-xs text-red-400 mt-2">The 21-day window has closed for your account.</p>}
            </>
          )}
        </div>

        <div className="glass rounded-2xl p-6" data-testid="salary-tracker">
          <h3 className="font-display font-bold text-lg mb-3 flex items-center gap-2"><Wallet className="w-5 h-5 text-amber-400" /> Weekly Salary</h3>
          {salary_tracker ? (
            <>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-slate-300">{salary_tracker.rank_name} · {money(salary_tracker.salary, 0)}/wk</span>
                <span className="font-mono-fin text-amber-300 font-bold text-sm">{salary_tracker.weeks_paid} / {salary_tracker.weeks_total} wk</span>
              </div>
              <div className="h-3 rounded-full bg-black/50 overflow-hidden border border-amber-500/20 mb-2">
                <div className="h-full gold-btn transition-all" style={{ width: `${(salary_tracker.weeks_paid / salary_tracker.weeks_total) * 100}%` }} />
              </div>
              <p className="text-sm">
                <span className="font-mono-fin text-emerald-400 font-bold">{salary_tracker.weeks_remaining}</span>
                <span className="text-slate-400"> weekly payment{salary_tracker.weeks_remaining === 1 ? "" : "s"} remaining at this rank</span>
              </p>
            </>
          ) : (
            <p className="text-sm text-slate-400">Reach <span className="text-amber-300 font-semibold">Master rank</span> ($5,000 matched business) to start earning a weekly salary.</p>
          )}
        </div>
      </div>

      {/* forex trade engine — gated: active ID + investment, unlocks ~45 min after investing */}
      {trading?.enabled && (trading?.streak || trading?.goal) && (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="trade-streak-goal">
          <div className="glass rounded-2xl p-6" data-testid="trade-streak">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg flex items-center gap-2"><Flame className="w-5 h-5 text-orange-400" /> Trading Streak</h3>
              <span className="text-xs px-2 py-1 rounded bg-orange-500/15 text-orange-400 font-bold">{trading.streak?.current || 0} day{(trading.streak?.current || 0) === 1 ? "" : "s"}</span>
            </div>
            <div className="flex items-end gap-2">
              <span className="font-mono-fin text-5xl font-extrabold text-orange-400" data-testid="streak-current">{trading.streak?.current || 0}</span>
              <span className="text-slate-400 text-sm mb-2">consecutive profitable day{(trading.streak?.current || 0) === 1 ? "" : "s"}</span>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="rounded-xl bg-black/30 border border-amber-500/15 p-3 text-center">
                <div className="font-mono-fin text-xl font-bold text-amber-300">{trading.streak?.best || 0}</div>
                <div className="text-xs text-slate-500">Best streak</div>
              </div>
              <div className="rounded-xl bg-black/30 border border-amber-500/15 p-3 text-center">
                <div className="font-mono-fin text-xl font-bold text-emerald-400">{trading.streak?.profitable_days || 0}</div>
                <div className="text-xs text-slate-500">Profitable days</div>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">Close a net-positive day to keep your streak alive. Markets rest on weekends. 🔥</p>
          </div>

          {!trading?.weekend && trading?.goal?.target > 0 && (
            <div className="glass rounded-2xl p-6" data-testid="trade-goal">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold text-lg flex items-center gap-2"><Target className="w-5 h-5 text-emerald-400" /> Today's Trade Goal</h3>
                <span className="text-xs px-2 py-1 rounded bg-emerald-500/15 text-emerald-400 font-bold" data-testid="goal-pct">{trading.goal?.pct || 0}%</span>
              </div>
              <div className="flex items-baseline justify-between text-sm mb-2">
                <span className="text-slate-400">Realized today</span>
                <span className="font-mono-fin"><span className="text-emerald-400 font-bold">{money(trading.goal?.realized || 0)}</span> <span className="text-slate-500">/ {money(trading.goal?.target || 0)}</span></span>
              </div>
              <div className="h-4 rounded-full bg-black/50 overflow-hidden border border-emerald-500/20">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all" style={{ width: `${Math.min(100, trading.goal?.pct || 0)}%` }} data-testid="goal-bar" />
              </div>
              <p className="text-xs text-slate-500 mt-3">Your closed trades add up toward today's Trade Profit target. Once you hit 100%, you're done for the day.</p>
            </div>
          )}
        </div>
      )}

      {trading?.enabled ? (
        <>
          {trading.performance?.length > 0 && (
            <div className="glass rounded-2xl p-6" data-testid="trade-performance">
              <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><Activity className="w-5 h-5 text-amber-400" /> Daily Performance</h3>
              <p className="text-sm text-slate-400 mb-4">Trading profit generated for your account each day. Your ROI is credited to you; the remainder is the platform's operating margin.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {trading.performance.map((p, i) => (
                  <div key={i} className="rounded-xl bg-[#0D0E12] border border-amber-500/20 p-5" data-testid={`perf-${i}`}>
                    <div className="flex justify-between items-center"><span className="font-display font-bold">{p.package_name}</span><span className="font-mono-fin text-amber-300">{money(p.principal, 0)}</span></div>
                    <div className="mt-3 space-y-1.5 text-sm">
                      <div className="flex justify-between"><span className="text-slate-400">Trade Profit</span><span className="font-mono-fin text-emerald-400 font-bold">{p.profit_min.toFixed(2)}%–{p.profit_max.toFixed(2)}%</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">Your ROI</span><span className="font-mono-fin text-amber-300">{p.roi_min.toFixed(2)}%–{p.roi_max.toFixed(2)}%</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">Company Profit</span><span className="font-mono-fin text-slate-400">0.10%</span></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <TradeTerminal onTradeClosed={() => setHistoryKey((k) => k + 1)} />
        </>
      ) : (
        <div className="glass rounded-2xl p-10 text-center" data-testid="trade-locked">
          <Activity className="w-12 h-12 text-amber-500/40 mx-auto mb-4" />
          <h3 className="font-display font-bold text-xl">Trade Engine Locked</h3>
          <p className="text-slate-400 text-sm mt-2 max-w-md mx-auto">{trading?.reason}</p>
          {trading?.unlock_at && (
            <p className="text-amber-300 font-mono-fin text-sm mt-3" data-testid="trade-unlock-time">
              Unlocks around {new Date(trading.unlock_at).toLocaleString()}
            </p>
          )}
          {trading?.reason?.includes("Activate your ID") && (
            <Link to="/portal/investments" className="inline-block mt-5 gold-btn px-6 py-3 rounded-xl font-bold text-sm">Buy a Package</Link>
          )}
        </div>
      )}

      {celebrate && <RankCelebration rankInfo={celebrate} userName={user.name} onClose={closeCelebration} />}
    </div>
  );
}
